import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  CloseOutlined,
  ArrowBackOutlined,
  AddCircleOutlineOutlined,
  CheckCircleOutlined,
  AddOutlined,
  RemoveOutlined,
  EditOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { CorpsMetier } from '../types/subscription';
import {
  PIECES_TEMPLATES,
  getPiecesForMetier,
  getPacksForPieceAndMetier,
  PieceTemplate,
  PackTravaux,
  TemplateLigne,
} from '../config/roomTemplates';
import { LigneDevis, TypeLigneDevis } from '../types';

interface RoomTemplatePickerProps {
  open: boolean;
  onClose: () => void;
  metier: CorpsMetier;
  onAddPack: (lignes: LigneDevis[]) => void;
}

type ViewMode = 'pieces' | 'packs';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const RoomTemplatePicker: React.FC<RoomTemplatePickerProps> = ({
  open,
  onClose,
  metier,
  onAddPack,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('pieces');
  const [selectedPiece, setSelectedPiece] = useState<PieceTemplate | null>(null);
  const [selectedPack, setSelectedPack] = useState<PackTravaux | null>(null);
  const [editableLignes, setEditableLignes] = useState<TemplateLigne[]>([]);

  // Réinitialiser la vue quand on ouvre le modal
  React.useEffect(() => {
    if (open) {
      setViewMode('pieces');
      setSelectedPiece(null);
      setSelectedPack(null);
      setEditableLignes([]);
    }
  }, [open]);

  const pieces = getPiecesForMetier(metier);
  const packs = selectedPiece ? getPacksForPieceAndMetier(selectedPiece.id, metier) : [];

  const handlePieceClick = (piece: PieceTemplate) => {
    setSelectedPiece(piece);
    setViewMode('packs');
  };

  const handleBackClick = () => {
    setViewMode('pieces');
    setSelectedPiece(null);
    setSelectedPack(null);
    setEditableLignes([]);
  };

  const handlePackClick = (pack: PackTravaux) => {
    setSelectedPack(pack);
    // Créer une copie des lignes du pack pour permettre l'édition
    setEditableLignes(JSON.parse(JSON.stringify(pack.lignes)));
  };

  // Fonction pour mettre à jour une ligne éditable
  const updateLigneEditable = (index: number, field: keyof TemplateLigne, value: any) => {
    const updatedLignes = [...editableLignes];
    updatedLignes[index] = {
      ...updatedLignes[index],
      [field]: value,
    };
    setEditableLignes(updatedLignes);
  };

  // Fonction pour supprimer une ligne
  const removeLigneEditable = (index: number) => {
    const updatedLignes = editableLignes.filter((_, i) => i !== index);
    setEditableLignes(updatedLignes);
  };

  // Fonction pour ajouter une nouvelle ligne vide
  const addLigneEditable = () => {
    const newLigne: TemplateLigne = {
      description: 'Nouvelle ligne',
      type: TypeLigneDevis.SURFACE,
      quantite: 1,
      prixUnitaire: 0,
      unite: 'm²',
      modeSaisieSurface: 'direct',
      avecPertes: false,
      tailleReference: '', // Champ pour la taille de carrelage
    };
    setEditableLignes([...editableLignes, newLigne]);
  };

  // Calculer le total estimé des lignes éditables
  const calculateTotalEstime = () => {
    return editableLignes.reduce((total, ligne) => {
      return total + (ligne.quantite * ligne.prixUnitaire);
    }, 0);
  };

  const handleAddPack = () => {
    if (!selectedPack) return;

    // Convertir les lignes éditables du template en lignes de devis
    const lignes: LigneDevis[] = editableLignes.map((templateLigne: TemplateLigne, index) => {
      const ligneDevis: LigneDevis = {
        id: `pack_${selectedPack.id}_${Date.now()}_${index}`,
        description: templateLigne.description,
        type: templateLigne.type,
        quantite: templateLigne.quantite,
        prixUnitaire: templateLigne.prixUnitaire,
        total: templateLigne.quantite * templateLigne.prixUnitaire,
        unite: templateLigne.unite,
      };

      // Ajouter les champs spécifiques selon le type de ligne
      if (templateLigne.type === TypeLigneDevis.SURFACE) {
        // Champs spécifiques aux surfaces
        ligneDevis.modeSaisieSurface = templateLigne.modeSaisieSurface;
        ligneDevis.avecPertes = templateLigne.avecPertes;

        // Copier tailleReference vers tailleCarrelage UNIQUEMENT pour Carreleur
        if (metier === CorpsMetier.CARRELEUR && templateLigne.tailleReference) {
          ligneDevis.tailleCarrelage = templateLigne.tailleReference;
        }

        // Pour les surfaces avec saisie directe, copier quantite dans surface
        if (templateLigne.modeSaisieSurface === 'direct') {
          ligneDevis.surface = templateLigne.quantite;
        }
      } else if (templateLigne.type === TypeLigneDevis.UNITAIRE || templateLigne.type === TypeLigneDevis.PIECE) {
        // Champs spécifiques aux types unitaire/pièce (taille de référence)
        // Copier tailleReference UNIQUEMENT pour Carreleur
        if (metier === CorpsMetier.CARRELEUR && templateLigne.tailleReference) {
          ligneDevis.tailleCarrelage = templateLigne.tailleReference;
        }
      }
      // Pour FORFAIT, pas de champs supplémentaires nécessaires

      return ligneDevis;
    });

    onAddPack(lignes);
    onClose();
  };

  // Variants pour les animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 24,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      scale: 0.95,
      transition: {
        duration: 0.2,
      },
    },
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 30,
      },
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      transition: {
        duration: 0.2,
      },
    }),
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '600px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#374151',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {viewMode === 'packs' && (
            <IconButton
              onClick={handleBackClick}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <ArrowBackOutlined />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {viewMode === 'pieces' ? '🏠 Choisir une pièce' : `${selectedPiece?.icone} ${selectedPiece?.nom} - Packs disponibles`}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <CloseOutlined />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4, minHeight: '500px' }}>
        <AnimatePresence mode="wait" custom={viewMode === 'packs' ? 1 : -1}>
          {viewMode === 'pieces' ? (
            <MotionBox
              key="pieces"
              custom={-1}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Sélectionnez une pièce pour voir les packs de travaux disponibles
              </Typography>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Grid container spacing={3}>
                  {pieces.map((piece) => (
                    <Grid item xs={12} sm={6} md={4} key={piece.id}>
                      <MotionCard
                        variants={itemVariants}
                        onClick={() => handlePieceClick(piece)}
                        sx={{
                          cursor: 'pointer',
                          height: '100%',
                          background: 'white',
                          borderRadius: 3,
                          border: '2px solid transparent',
                          transition: 'all 0.3s',
                          '&:hover': {
                            borderColor: '#F59E0B',
                            boxShadow: '0 8px 24px rgba(245, 158, 11, 0.25)',
                            transform: 'translateY(-4px)',
                          },
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                          <Typography
                            variant="h2"
                            sx={{ mb: 2, fontSize: '4rem' }}
                          >
                            {piece.icone}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            {piece.nom}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {piece.description}
                          </Typography>
                          <Chip
                            label={`${getPacksForPieceAndMetier(piece.id, metier).length} pack(s) disponible(s)`}
                            size="small"
                            sx={{ mt: 2 }}
                            color="primary"
                          />
                        </CardContent>
                      </MotionCard>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </MotionBox>
          ) : (
            <MotionBox
              key="packs"
              custom={1}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Grid container spacing={3}>
                  {/* Liste des packs */}
                  <Grid item xs={12} md={selectedPack ? 6 : 12}>
                    <Box>
                      {packs.length === 0 ? (
                        <Typography variant="body1" color="text.secondary">
                          Aucun pack disponible pour cette pièce et ce métier.
                        </Typography>
                      ) : (
                        <Grid container spacing={2}>
                          {packs.map((pack) => (
                            <Grid item xs={12} key={pack.id}>
                              <MotionCard
                                variants={itemVariants}
                                onClick={() => handlePackClick(pack)}
                                sx={{
                                  cursor: 'pointer',
                                  background: 'white',
                                  borderRadius: 2,
                                  border: selectedPack?.id === pack.id ? '3px solid #F59E0B' : '2px solid transparent',
                                  transition: 'all 0.3s',
                                  '&:hover': {
                                    borderColor: selectedPack?.id === pack.id ? '#F59E0B' : '#D1D5DB',
                                    boxShadow: selectedPack?.id === pack.id
                                      ? '0 8px 24px rgba(245, 158, 11, 0.35)'
                                      : '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  },
                                }}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                              >
                                <CardContent>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="h4">{pack.icone}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                          {pack.nom}
                                        </Typography>
                                      </Box>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {pack.description}
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                          label={`${pack.lignes.length} ligne(s)`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                        <Chip
                                          label={`≈ ${pack.prixEstime.toFixed(0)} € HT`}
                                          size="small"
                                          color="success"
                                        />
                                      </Box>
                                    </Box>
                                    {selectedPack?.id === pack.id && (
                                      <CheckCircleOutlined
                                        sx={{ color: '#F59E0B', fontSize: 32 }}
                                      />
                                    )}
                                  </Box>
                                </CardContent>
                              </MotionCard>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  </Grid>

                  {/* Détails du pack sélectionné */}
                  {selectedPack && (
                    <Grid item xs={12} md={6}>
                      <AnimatePresence mode="wait">
                        <MotionBox
                          key={selectedPack.id}
                          initial={{ opacity: 0, x: 50 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 50 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        >
                          <Card
                            sx={{
                              background: 'white',
                              borderRadius: 3,
                              border: '2px solid #F59E0B',
                              boxShadow: '0 8px 24px rgba(245, 158, 11, 0.25)',
                              position: 'sticky',
                              top: 20,
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography variant="h3">{selectedPack.icone}</Typography>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {selectedPack.nom}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Détails du pack
                                  </Typography>
                                </Box>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  📋 Lignes incluses ({editableLignes.length})
                                </Typography>

                                {/* Info éditable - discrète et professionnelle */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    bgcolor: '#EEF2FF',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 2,
                                    border: '1px solid #C7D2FE',
                                  }}
                                >
                                  <EditOutlined sx={{ fontSize: 14, color: '#6366F1' }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#4F46E5', fontWeight: 500 }}>
                                    Valeurs modifiables
                                  </Typography>
                                </Box>
                              </Box>

                              <List dense sx={{ bgcolor: '#F9FAFB', borderRadius: 2, p: 1 }}>
                                {editableLignes.map((ligne, index) => (
                                  <ListItem
                                    key={index}
                                    sx={{
                                      bgcolor: 'white',
                                      borderRadius: 1,
                                      mb: 0.5,
                                      border: '1px solid #E5E7EB',
                                      pr: 1,
                                    }}
                                    secondaryAction={
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={() => removeLigneEditable(index)}
                                        sx={{
                                          opacity: 0.4,
                                          transition: 'all 0.2s',
                                          '&:hover': {
                                            opacity: 1,
                                            bgcolor: '#FFEBEE',
                                            color: 'error.main',
                                          },
                                        }}
                                      >
                                        <CloseOutlined sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        <TextField
                                          value={ligne.description}
                                          onChange={(e) => updateLigneEditable(index, 'description', e.target.value)}
                                          variant="standard"
                                          fullWidth
                                          InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                              fontWeight: 500,
                                              fontSize: '0.875rem',
                                              '&:hover': {
                                                bgcolor: '#F3F4F6',
                                                borderRadius: 1,
                                                px: 0.5,
                                              },
                                            }
                                          }}
                                          sx={{ mb: 0.5 }}
                                        />
                                      }
                                      secondary={
                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                          {/* Type/Unité */}
                                          <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            bgcolor: '#F3E5F5',
                                            borderRadius: '16px',
                                            px: 1,
                                            height: 20,
                                            '&:hover': { bgcolor: '#E1BEE7' }
                                          }}>
                                            <Select
                                              value={ligne.type || TypeLigneDevis.SURFACE}
                                              onChange={(e) => {
                                                const newType = e.target.value as TypeLigneDevis;
                                                updateLigneEditable(index, 'type', newType);
                                                // Mettre à jour l'unité en fonction du type
                                                if (newType === TypeLigneDevis.SURFACE) {
                                                  updateLigneEditable(index, 'unite', 'm²');
                                                } else if (newType === TypeLigneDevis.UNITAIRE) {
                                                  updateLigneEditable(index, 'unite', 'ml');
                                                } else if (newType === TypeLigneDevis.PIECE) {
                                                  updateLigneEditable(index, 'unite', 'pièce');
                                                } else if (newType === TypeLigneDevis.FORFAIT) {
                                                  updateLigneEditable(index, 'unite', 'forfait');
                                                }
                                              }}
                                              variant="standard"
                                              disableUnderline
                                              sx={{
                                                fontSize: '0.7rem',
                                                '& .MuiSelect-select': {
                                                  padding: 0,
                                                  paddingRight: '16px !important',
                                                  fontSize: '0.7rem',
                                                  textAlign: 'center',
                                                  minWidth: '35px',
                                                },
                                                '& .MuiSelect-icon': {
                                                  fontSize: '0.9rem',
                                                  right: -2,
                                                }
                                              }}
                                            >
                                              <MenuItem value={TypeLigneDevis.SURFACE} sx={{ fontSize: '0.75rem' }}>m²</MenuItem>
                                              <MenuItem value={TypeLigneDevis.UNITAIRE} sx={{ fontSize: '0.75rem' }}>ml</MenuItem>
                                              <MenuItem value={TypeLigneDevis.PIECE} sx={{ fontSize: '0.75rem' }}>pièce</MenuItem>
                                              <MenuItem value={TypeLigneDevis.FORFAIT} sx={{ fontSize: '0.75rem' }}>forfait</MenuItem>
                                            </Select>
                                          </Box>

                                          {/* Quantité */}
                                          <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            bgcolor: '#E3F2FD',
                                            borderRadius: '16px',
                                            px: 1,
                                            height: 20,
                                            '&:hover': { bgcolor: '#BBDEFB' }
                                          }}>
                                            <TextField
                                              type="number"
                                              value={ligne.quantite}
                                              onChange={(e) => updateLigneEditable(index, 'quantite', parseFloat(e.target.value) || 0)}
                                              variant="standard"
                                              inputProps={{
                                                min: 0,
                                                step: 0.01,
                                                style: {
                                                  textAlign: 'center',
                                                  fontSize: '0.7rem',
                                                  padding: 0,
                                                  width: '40px',
                                                }
                                              }}
                                              InputProps={{
                                                disableUnderline: true,
                                              }}
                                            />
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', ml: 0.3 }}>
                                              {ligne.unite || ''}
                                            </Typography>
                                          </Box>

                                          {/* Prix unitaire */}
                                          <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            bgcolor: '#FFF3E0',
                                            borderRadius: '16px',
                                            px: 1,
                                            height: 20,
                                            '&:hover': { bgcolor: '#FFE0B2' }
                                          }}>
                                            <TextField
                                              type="number"
                                              value={ligne.prixUnitaire}
                                              onChange={(e) => updateLigneEditable(index, 'prixUnitaire', parseFloat(e.target.value) || 0)}
                                              variant="standard"
                                              inputProps={{
                                                min: 0,
                                                step: 0.01,
                                                style: {
                                                  textAlign: 'center',
                                                  fontSize: '0.7rem',
                                                  padding: 0,
                                                  width: '45px',
                                                }
                                              }}
                                              InputProps={{
                                                disableUnderline: true,
                                              }}
                                            />
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', ml: 0.3 }}>
                                              € × {ligne.quantite} = {(ligne.quantite * ligne.prixUnitaire).toFixed(2)}€
                                            </Typography>
                                          </Box>

                                          {/* Taille de carrelage - Uniquement pour Carreleur */}
                                          {metier === CorpsMetier.CARRELEUR && (ligne.type === TypeLigneDevis.SURFACE || ligne.tailleReference !== undefined) && (
                                            <Box sx={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              border: '1px solid #E0E0E0',
                                              borderRadius: '16px',
                                              px: 1,
                                              height: 20,
                                              '&:hover': { bgcolor: '#F5F5F5' }
                                            }}>
                                              <TextField
                                                value={ligne.tailleReference || ''}
                                                onChange={(e) => updateLigneEditable(index, 'tailleReference', e.target.value)}
                                                variant="standard"
                                                placeholder="60x60"
                                                inputProps={{
                                                  style: {
                                                    textAlign: 'center',
                                                    fontSize: '0.7rem',
                                                    padding: 0,
                                                    width: '50px',
                                                  }
                                                }}
                                                InputProps={{
                                                  disableUnderline: true,
                                                }}
                                              />
                                            </Box>
                                          )}
                                        </Box>
                                      }
                                      primaryTypographyProps={{
                                        sx: { fontWeight: 500, fontSize: '0.875rem' },
                                      }}
                                    />
                                  </ListItem>
                                ))}

                                {/* Bouton pour ajouter une nouvelle ligne */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
                                    onClick={addLigneEditable}
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '0.75rem',
                                      color: '#666',
                                      opacity: 0.6,
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        opacity: 1,
                                        bgcolor: '#E8F5E9',
                                        color: 'success.main',
                                      },
                                    }}
                                  >
                                    Ajouter une ligne
                                  </Button>
                                </Box>
                              </List>

                              <Divider sx={{ my: 2 }} />

                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Total estimé HT
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#059669' }}>
                                  {calculateTotalEstime().toFixed(2)} €
                                </Typography>
                              </Box>

                              {/* Info avant ajout */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 1.5,
                                  mb: 2,
                                  bgcolor: '#F0FDF4',
                                  borderRadius: 2,
                                  border: '1px dashed #86EFAC',
                                }}
                              >
                                <InfoOutlined sx={{ fontSize: 18, color: '#16A34A' }} />
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#15803D', lineHeight: 1.4 }}>
                                  Vous pouvez personnaliser toutes les valeurs avant d'ajouter le pack
                                </Typography>
                              </Box>

                              <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                startIcon={<AddCircleOutlineOutlined />}
                                onClick={handleAddPack}
                                sx={{
                                  py: 1.5,
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  bgcolor: '#F59E0B',
                                  '&:hover': {
                                    bgcolor: '#D97706',
                                  },
                                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                                }}
                              >
                                Ajouter ce pack au devis
                              </Button>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                              >
                                Les {editableLignes.length} lignes seront ajoutées à votre devis
                              </Typography>
                            </CardContent>
                          </Card>
                        </MotionBox>
                      </AnimatePresence>
                    </Grid>
                  )}
                </Grid>
              </motion.div>
            </MotionBox>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default RoomTemplatePicker;
